import {
  at,
  createMatrix,
  type Matrix,
  maxAbs,
  normalMatrix,
  set,
  solveSymmetric,
  sumOfSquares,
  transposeMultiplyVector,
} from '@/domain/math/linear';

/**
 * Levenberg–Marquardt least squares.
 *
 * LM interpolates between two methods by a damping parameter `λ`:
 *
 * - `λ → 0` gives Gauss–Newton, which converges quadratically near a solution but
 *   diverges when started far away or when `JᵀJ` is near-singular — and a
 *   near-singular `JᵀJ` is exactly what an under-constrained sketch produces, which is
 *   the normal state of a drawing the user is still building.
 * - `λ → ∞` gives gradient descent, which always makes progress but crawls.
 *
 * Raising `λ` after a rejected step and lowering it after an accepted one gets both
 * properties. That robustness is why LM rather than plain Newton: a geometry editor
 * must not fail to converge because the user has not finished constraining a shape.
 *
 * Damping is uniform and scaled to the largest diagonal of `JᵀJ`; see `applyDamping`
 * for why Marquardt's per-variable scaling is the wrong choice for this problem.
 */

export interface LeastSquaresProblem {
  readonly variableCount: number;
  readonly residualCount: number;
  /**
   * Writes residuals and the Jacobian for the given variable vector.
   *
   * Both output buffers are reused across iterations and must be fully overwritten —
   * the solver does not clear them, because clearing a dense Jacobian every iteration is
   * pure waste when the assembler writes every entry anyway.
   */
  evaluate(x: Float64Array, residuals: Float64Array, jacobian: Matrix): void;
}

export interface SolveOptions {
  readonly maxIterations?: number;
  /** Stop when the largest component of `Jᵀr` falls below this. */
  readonly gradientTolerance?: number;
  /** Stop when the sum of squared residuals falls below this. */
  readonly residualTolerance?: number;
  /** Stop when a step changes the variables by less than this. */
  readonly stepTolerance?: number;
  readonly initialDamping?: number;
}

export const SolveStatus = {
  /** Residuals driven below tolerance: the constraints are satisfied. */
  Converged: 'converged',
  /** A stationary point that still has residual — over-constrained, or a local minimum. */
  Stalled: 'stalled',
  /** Ran out of iterations while still improving. */
  MaxIterations: 'max-iterations',
} as const;
export type SolveStatus = (typeof SolveStatus)[keyof typeof SolveStatus];

export interface SolveResult {
  readonly x: Float64Array;
  readonly status: SolveStatus;
  readonly iterations: number;
  /** Sum of squared residuals at the returned point. */
  readonly error: number;
}

const DEFAULTS = {
  maxIterations: 60,
  gradientTolerance: 1e-12,
  residualTolerance: 1e-18,
  stepTolerance: 1e-14,
  initialDamping: 1e-3,
};

export function solveLeastSquares(
  problem: LeastSquaresProblem,
  initial: Float64Array,
  options: SolveOptions = {}
): SolveResult {
  const maxIterations = options.maxIterations ?? DEFAULTS.maxIterations;
  const gradientTolerance = options.gradientTolerance ?? DEFAULTS.gradientTolerance;
  const residualTolerance = options.residualTolerance ?? DEFAULTS.residualTolerance;
  const stepTolerance = options.stepTolerance ?? DEFAULTS.stepTolerance;

  const n = problem.variableCount;
  const m = problem.residualCount;

  const x = Float64Array.from(initial);
  if (n === 0 || m === 0) {
    return { x, status: SolveStatus.Converged, iterations: 0, error: 0 };
  }

  const residuals = new Float64Array(m);
  const trialResiduals = new Float64Array(m);
  const jacobian = createMatrix(m, n);
  const trial = new Float64Array(n);

  let damping = options.initialDamping ?? DEFAULTS.initialDamping;
  let iterations = 0;

  problem.evaluate(x, residuals, jacobian);
  let error = sumOfSquares(residuals);

  while (iterations < maxIterations) {
    if (error <= residualTolerance) {
      return { x, status: SolveStatus.Converged, iterations, error };
    }

    const normal = normalMatrix(jacobian);
    const gradient = transposeMultiplyVector(jacobian, residuals);

    if (maxAbs(gradient) <= gradientTolerance) {
      // A stationary point. Zero residual means solved; non-zero means the cluster
      // cannot be satisfied and this is the least-squares compromise.
      return {
        x,
        status: error <= residualTolerance ? SolveStatus.Converged : SolveStatus.Stalled,
        iterations,
        error,
      };
    }

    // Inner loop: raise the damping until a step is accepted.
    let stepAccepted = false;
    for (let attempt = 0; attempt < 30 && !stepAccepted; attempt++) {
      const damped = applyDamping(normal, damping);

      // Solving for -gradient gives the descent direction.
      const negativeGradient = new Float64Array(n);
      for (let i = 0; i < n; i++) negativeGradient[i] = -(gradient[i] ?? 0);

      const step = solveSymmetric(damped, negativeGradient);
      if (step === null) {
        // Not positive definite even with damping: damp harder.
        damping *= 10;
        continue;
      }

      let stepLength = 0;
      for (let i = 0; i < n; i++) {
        const value = step[i] ?? 0;
        trial[i] = (x[i] ?? 0) + value;
        stepLength += value * value;
      }

      if (Math.sqrt(stepLength) <= stepTolerance) {
        return {
          x,
          status: error <= residualTolerance ? SolveStatus.Converged : SolveStatus.Stalled,
          iterations,
          error,
        };
      }

      problem.evaluate(trial, trialResiduals, jacobian);
      const trialError = sumOfSquares(trialResiduals);

      if (trialError < error) {
        x.set(trial);
        residuals.set(trialResiduals);
        error = trialError;
        // Trust the linear model a little more next time.
        damping = Math.max(damping / 3, 1e-12);
        stepAccepted = true;
      } else {
        // The step overshot; the Jacobian was left holding the trial point's values, so
        // it must be recomputed at `x` before the next outer iteration uses it.
        damping *= 3;
      }
    }

    iterations++;

    if (!stepAccepted) {
      problem.evaluate(x, residuals, jacobian);
      return {
        x,
        status: error <= residualTolerance ? SolveStatus.Converged : SolveStatus.Stalled,
        iterations,
        error,
      };
    }

    problem.evaluate(x, residuals, jacobian);
  }

  return {
    x,
    status: error <= residualTolerance ? SolveStatus.Converged : SolveStatus.MaxIterations,
    iterations,
    error,
  };
}

/**
 * `JᵀJ + λ·max(diag(JᵀJ))·I` — Levenberg's uniform damping, scaled to the problem.
 *
 * Marquardt's refinement damps each variable by *its own* diagonal entry, which is the
 * right choice when variables carry different units. Here it is actively wrong, and
 * measurably so.
 *
 * An under-constrained cluster — the normal state of a sketch someone is still building —
 * makes `JᵀJ` rank-deficient, and a null-space direction shows up as a near-zero diagonal
 * entry. Damping that entry *by itself* adds almost nothing, so the solve is free to put
 * an enormous component along the direction the data says nothing about. Measured on a
 * single "point lies on a circle of radius 5" constraint from (20, 1): the intended step
 * is `(−15, −0.75)`; per-variable damping produced `(−7.5, −150)` and threw the point
 * twenty times further away, after which every trial step was rejected and the solver
 * crawled.
 *
 * A uniform `λ·max(diag)` regularises every direction equally, which is what a
 * rank-deficient system needs. Nothing is lost by dropping the per-variable scaling
 * because every variable here is a world-space coordinate in the same units.
 *
 * The absolute floor covers the remaining case: a `JᵀJ` that is entirely zero, which
 * happens when no residual depends on any variable.
 */
function applyDamping(normal: Matrix, damping: number): Matrix {
  const n = normal.rows;
  const damped = createMatrix(n, n);
  damped.data.set(normal.data);

  let largestDiagonal = 0;
  for (let i = 0; i < n; i++) {
    const value = at(normal, i, i);
    if (value > largestDiagonal) largestDiagonal = value;
  }
  const scale = Math.max(largestDiagonal, 1e-9);

  for (let i = 0; i < n; i++) {
    set(damped, i, i, at(normal, i, i) + damping * scale);
  }
  return damped;
}
